import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, ExternalLink, Megaphone, Sparkles, X } from "lucide-react";
import { api, apiError } from "@/lib/api";

const CATEGORY = {
  free: { label: "Free", cls: "bg-emerald-50 text-veg border-veg/30" },
  credit: { label: "Promotional Credit", cls: "bg-amber-50 text-amber-700 border-amber-300" },
  paid: { label: "Paid", cls: "bg-red-50 text-nonveg border-nonveg/30" },
};
const STATUSES = ["not_started", "in_progress", "active", "expired", "skipped"];
const STATUS_CLS = {
  not_started: "bg-surface text-ink-600 border-border",
  in_progress: "bg-blue-50 text-blue-600 border-blue-200",
  active: "bg-emerald-50 text-veg border-veg/30",
  expired: "bg-red-50 text-nonveg border-nonveg/30",
  skipped: "bg-ink/5 text-ink-600 border-border",
};
const EMPTY = { platform: "", promo_type: "", category: "free", eligibility: "", expiry_date: "", url: "", status: "not_started", notes: "" };

export default function AdminPromotions() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState(null); // null | {...} (add/edit)
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/promotions").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const seed = async () => {
    try { const { data } = await api.post("/admin/promotions/seed"); toast.success(data.seeded ? `Added ${data.seeded} starter opportunities` : data.message); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const save = async () => {
    if (!form.platform?.trim()) return toast.error("Platform is required");
    setSaving(true);
    try {
      const payload = { ...form, expiry_date: form.expiry_date || null };
      if (form.id) await api.put(`/admin/promotions/${form.id}`, payload);
      else await api.post("/admin/promotions", payload);
      toast.success("Saved"); setForm(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const del = async (id) => { try { await api.delete(`/admin/promotions/${id}`); toast.success("Removed"); load(); } catch (e) { toast.error(apiError(e)); } };

  const shown = filter === "all" ? list : list.filter((p) => p.category === filter);
  const counts = { free: list.filter((p) => p.category === "free").length, credit: list.filter((p) => p.category === "credit").length, paid: list.filter((p) => p.category === "paid").length };

  return (
    <div className="space-y-4" data-testid="admin-promotions">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2"><Megaphone size={20} className="text-coral" /><h2 className="font-display font-bold text-xl text-ink">Free Promotion Opportunities</h2></div>
        <div className="ml-auto flex gap-2">
          {list.length === 0 && <button onClick={seed} className="flex items-center gap-1.5 bg-ink text-white font-bold text-sm px-3 py-2 rounded-xl tap" data-testid="seed-promos"><Sparkles size={15} /> Load starter list</button>}
          <button onClick={() => setForm({ ...EMPTY })} className="flex items-center gap-1.5 bg-coral text-white font-bold text-sm px-3 py-2 rounded-xl tap" data-testid="add-promo"><Plus size={16} /> Add opportunity</button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 text-xs">
        {[["all", `All (${list.length})`], ["free", `Free (${counts.free})`], ["credit", `Promotional Credit (${counts.credit})`], ["paid", `Paid (${counts.paid})`]].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1.5 rounded-full font-semibold border ${filter === k ? "bg-coral text-white border-coral" : "bg-white border-border text-ink-600"}`} data-testid={`promo-filter-${k}`}>{l}</button>
        ))}
      </div>

      {shown.length === 0 && <div className="bg-white rounded-2xl border border-border p-8 text-center text-ink-600">No opportunities yet. Use “Load starter list” for a curated set of legitimate free and promotional-credit options.</div>}

      <div className="grid gap-3 md:grid-cols-2">
        {shown.map((p) => (
          <div key={p.id} className="bg-white rounded-2xl border border-border p-4" data-testid={`promo-${p.id}`}>
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <div className="font-bold text-ink flex items-center gap-2 flex-wrap">{p.platform}
                  <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border ${CATEGORY[p.category]?.cls || CATEGORY.free.cls}`}>{CATEGORY[p.category]?.label || p.category}</span>
                </div>
                {p.promo_type && <div className="text-xs text-ink-600 mt-0.5">{p.promo_type}</div>}
              </div>
              <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded border ${STATUS_CLS[p.status] || STATUS_CLS.not_started}`}>{(p.status || "").replace("_", " ")}</span>
            </div>
            {p.eligibility && <div className="text-xs text-ink-600 mt-2"><span className="font-semibold text-ink">Eligibility:</span> {p.eligibility}</div>}
            {p.expiry_date && <div className="text-xs text-ink-600 mt-0.5"><span className="font-semibold text-ink">Expires:</span> {p.expiry_date}</div>}
            {p.notes && <div className="text-xs text-ink-600 mt-0.5 italic">{p.notes}</div>}
            <div className="flex items-center gap-3 mt-3 pt-3 border-t border-border">
              {p.url && <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-coral font-semibold text-xs flex items-center gap-1" data-testid={`promo-link-${p.id}`}><ExternalLink size={13} /> Open platform</a>}
              <button onClick={() => setForm({ ...p, expiry_date: p.expiry_date || "" })} className="ml-auto text-ink-600 text-xs font-semibold flex items-center gap-1" data-testid={`promo-edit-${p.id}`}><Pencil size={13} /> Edit</button>
              <button onClick={() => del(p.id)} className="text-nonveg text-xs font-semibold flex items-center gap-1" data-testid={`promo-del-${p.id}`}><Trash2 size={13} /> Delete</button>
            </div>
          </div>
        ))}
      </div>

      {form && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" onClick={() => setForm(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="promo-form">
            <div className="flex items-center justify-between mb-3"><h3 className="font-display font-bold text-lg text-ink">{form.id ? "Edit" : "Add"} opportunity</h3><button onClick={() => setForm(null)}><X size={20} /></button></div>
            <div className="space-y-2.5">
              <L label="Platform *"><input value={form.platform} onChange={(e) => setForm((f) => ({ ...f, platform: e.target.value }))} className={inp} data-testid="pf-platform" placeholder="e.g. Google Business Profile" /></L>
              <L label="Promotion type"><input value={form.promo_type} onChange={(e) => setForm((f) => ({ ...f, promo_type: e.target.value }))} className={inp} placeholder="e.g. Local listing / Ad credit" /></L>
              <div className="grid grid-cols-2 gap-2">
                <L label="Category"><select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className={inp} data-testid="pf-category"><option value="free">Free</option><option value="credit">Promotional Credit</option><option value="paid">Paid</option></select></L>
                <L label="Status"><select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} className={inp} data-testid="pf-status">{STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}</select></L>
              </div>
              <L label="Eligibility"><input value={form.eligibility} onChange={(e) => setForm((f) => ({ ...f, eligibility: e.target.value }))} className={inp} /></L>
              <div className="grid grid-cols-2 gap-2">
                <L label="Expiry date"><input type="date" value={form.expiry_date} onChange={(e) => setForm((f) => ({ ...f, expiry_date: e.target.value }))} className={inp} /></L>
                <L label="Official URL"><input value={form.url} onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))} className={inp} placeholder="https://" /></L>
              </div>
              <L label="Notes"><textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} rows={2} className={inp} /></L>
            </div>
            <button onClick={save} disabled={saving} className="w-full bg-coral text-white font-bold py-2.5 rounded-xl mt-4 tap disabled:opacity-60" data-testid="pf-save">{saving ? "Saving…" : "Save opportunity"}</button>
          </div>
        </div>
      )}
    </div>
  );
}

const inp = "w-full bg-surface rounded-lg px-3 py-2 text-sm outline-none border border-border";
const L = ({ label, children }) => (<div><label className="text-[11px] font-semibold text-ink-600 mb-0.5 block">{label}</label>{children}</div>);
